import React from "react";
import { Box, FormControl, Grid, Typography, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { authenticationTokenRequestSchema, type AuthenticationTokenRequestData } from "../../../schemas";
import { ApplicationWordArt, AuthenticationButton, StyledFormLabel, StyledTextField } from "../../../components";

export interface LoginTokenFormProps {
	readonly onSubmit: (data: AuthenticationTokenRequestData) => Promise<void>;
	readonly isError: boolean;
}

export const LoginTokenForm: React.FC<LoginTokenFormProps> = ({ onSubmit, isError }) => {
	const intl = useIntl();

	const {
		register,
		handleSubmit,
		formState: { isSubmitting, errors },
	} = useForm<AuthenticationTokenRequestData>({
		resolver: zodResolver(authenticationTokenRequestSchema(intl)),
	});

	return (
		<Grid container component="form" onSubmit={handleSubmit(onSubmit)} direction="column" gap={6}>
			<Box sx={{ margin: "auto" }}>
				<ApplicationWordArt width={300} />
			</Box>

			<Grid container direction="column" alignItems="center">
				<Typography variant="h1" color="white">
					<FormattedMessage id="login-token.title" defaultMessage="Token login" />
				</Typography>
				<Typography color="white">
					<FormattedMessage id="login-token.subtitle" defaultMessage="Enter your token to continue" />
				</Typography>
			</Grid>

			<Grid container direction="column" gap={2}>
				<FormControl error={Boolean(errors.token) || isError}>
					<StyledFormLabel color="white">
						<FormattedMessage id="login-token.token" defaultMessage="Token" />
					</StyledFormLabel>

					<StyledTextField
						placeholder={intl.formatMessage({
							id: "login-token.token.placeholder",
							defaultMessage: "Enter your token",
						})}
						fullWidth
						autoFocus
						error={Boolean(errors.token) || isError}
						helperText={errors.token?.message}
						slotProps={{
							htmlInput: {
								"data-testid": "login-token-form__input__token-input",
							},
						}}
						{...register("token")}
					/>
				</FormControl>

				{Boolean(isError) && (
					<Typography color="error" data-testid="login-token__text__error">
						<FormattedMessage
							id="login-token.error"
							defaultMessage="Unable to login using the supplied token. Please try again."
						/>
					</Typography>
				)}

				<Grid container gap={1} direction="column">
					<AuthenticationButton
						variant="contained"
						type="submit"
						fullWidth
						size="large"
						loading={isSubmitting}
						data-testid="login-token-form__button__submit"
					>
						<FormattedMessage id="login-token.submit" defaultMessage="Login" />
					</AuthenticationButton>

					<Link to="/login" component={RouterLink} sx={{ alignSelf: "flex-end", color: "white" }}>
						<FormattedMessage id="login-token.back" defaultMessage="Back to login" />
					</Link>
				</Grid>
			</Grid>
		</Grid>
	);
};
